











































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import { descending } from 'd3-array';
import { timeDay } from 'd3-time';
import { INode as ITreeNode } from '@/components/Vis/D3/Charts/TreeBranch';
import { IReactivationStats, ISessionOutcomeCount, IState, SaveType, ISegment } from '@/store/types';
import { LOAD_STATE, OFFER_TYPE, SAVE_TYPE } from '@/store/shared';
import { EXPORT_DATA, FETCH_SESSION_OUTCOMES, RETURN_SESSION_OUTCOMES } from '@/store/action-types';
import { dateTimeLabelFormat, getDateEndpoints } from '@/helpers/date-utils';
import { formatMoney, formatPercent, formatSmallMoney } from '@/helpers/jsUtils';
import { SESSION } from '@/helpers/stats-utils';
import { ISankeyLink, ISankeyNode, Sankey } from '@/components/Vis/D3/Charts/Sankey';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import TreeBranchWrapper from '@/components/Vis/D3/Charts/TreeBranchWrapper.vue';
import StatsService from '@/api/stats.service';
import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import SankeyWrapper from '@/components/Vis/D3/Charts/SankeyWrapper.vue';
import EmptySankey from '@/components/UI/Empty/EmptySankey.vue';
import SankeyLoader from '@/components/UI/Loader/SankeyLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import FooterLoader from '@/components/UI/Loader/FooterLoader.vue';

interface ReactivationBreakdown {
  label: string;
  primary: string;
  sub: string;
  subColor: string;
  reactivationRate: number | null;
  reactivated: number;
  total: number;
  boostedRevenue: number;
}

export default Vue.extend({
  name: 'SessionOutcomes',
  components: {
    TreeBranchWrapper,
    SankeyWrapper,
    DatePickerCondensed,
    SessionFilter,
    EmptySankey,
    HeaderLoader,
    SankeyLoader,
    FooterLoader,
  },
  async created() {
    if (this.selectedSegment) {
      this.filter = {
        segmentId: [this.selectedSegment],
      };
    }
    if (this.abtest) {
      this.filter.abtest = [this.abtest];
    }
    this.fetchReactivationStats();
    await this.fetchOutcomes();
    this.initialLoading = false;
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -90);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      startDate,
      sessionOutcomes: [] as ISessionOutcomeCount[],
      filteredOutcomes: [] as ISessionOutcomeCount[],
      endDate,
      loading: true,
      sessionSegmentSelection: null,
      filter: {} as Record<string, string[]>,
      formatMoney,
      formatPercent,
      initialLoading: true,
      chartInstance: null as Sankey | null,
      localReactivationStats: [] as IReactivationStats[],
      localReactivationStatsLoadState: LOAD_STATE.UNLOADED,
      LOAD_STATE,
    };
  },
  props: {
    abtest: {
      type: String,
    },
    selectedSegment: {
      type: String,
    },
  },
  computed: {
    ...mapGetters(['couponIdToString', 'sessionSegments', 'orgCreatedDate', 'daysSinceOrgCreated']),
    formattedDateSelection(): string {
      return `from ${dateTimeLabelFormat(this.startDate)} to ${dateTimeLabelFormat(this.endDate)}`;
    },
    statLines(): ReactivationBreakdown[] {
      if (this.localReactivationStatsLoadState !== LOAD_STATE.LOADED || this.localReactivationStats.length === 0) {
        return [];
      }
      if (this.localReactivationStats) {
        const statsToShow = [
          [SAVE_TYPE.PAUSE, 'Avg. Boost After Pause'],
          [SAVE_TYPE.DISCOUNT, 'Avg. Boost After Discount'],
          [SAVE_TYPE.ABANDON, 'Avg. Boost After Abandon'],
          [SAVE_TYPE.TRIAL_EXTENSION, 'Avg. Boost After Trial Extension'],
          [SAVE_TYPE.PLAN_CHANGE, 'Avg. Boost After Plan Change'],
          [SAVE_TYPE.CONTACT, 'Avg. Boost After Support'],
        ] as [SaveType, string][];

        const acc = [] as ReactivationBreakdown[];
        statsToShow.forEach(([saveType, label]) => {
          const d = this.localReactivationStats
            // .filter((s) => (this.subscriptionStatus === 'trialing' ? s.trial : !s.trial))
            .find((s) => s.saveType === saveType);
          if (d) {
            let avgBoost = 0;
            if (d.reactivated) {
              avgBoost = d.boostedRevenue / d.reactivated;
            }
            acc.push({
              label,
              primary: avgBoost ? `${formatSmallMoney(avgBoost)}+` : '-',
              sub: avgBoost ? `on ${formatPercent(d.reactivationRate)} reactivation rate` : '',
              subColor: 'text-gray-500 text-sm',
              ...d,
            });
          }
        });
        const show = acc.reduce((test, d) => d.primary !== '-' || test, false);
        if (!show) {
          return [];
        }
        return acc;
      }
      return [];
    },
    sankey() {
      if (this.loading || !this.filteredOutcomes.length) {
        return null;
      }
      const canceledOutcome = this.filteredOutcomes.find((d) => d.canceled);
      const abandonedOutcome = this.filteredOutcomes.find((d) => d.aborted);
      const pauseOutcomes = this.filteredOutcomes.filter((d) => d.pauseDuration).sort((a, b) => descending(a.pauseDuration, b.pauseDuration));
      const discountOutcomes = this.filteredOutcomes.filter((d) => d.couponId).sort((a, b) => descending(a.count, b.count));
      const contactOutcome = this.filteredOutcomes.find((d) => d.offerType === OFFER_TYPE.CONTACT);
      const extendedTrialOutcome = this.filteredOutcomes.find((d) => d.offerType === OFFER_TYPE.TRIAL_EXTENSION);
      const changedPlanOutcome = this.filteredOutcomes.find((d) => d.offerType === OFFER_TYPE.PLAN_CHANGE);
      const redirectedOutcome = this.filteredOutcomes.find((d) => d.offerType === OFFER_TYPE.REDIRECT);

      const totalSessions = this.filteredOutcomes.reduce((sum, d) => sum + d.count, 0);
      const totalCanceled = canceledOutcome?.count || 0;
      const totalSaved = totalSessions - totalCanceled;

      const retainedPct = totalSessions ? totalSaved / totalSessions : 0;
      const canceledPct = totalSessions ? totalCanceled / totalSessions : 0;

      const nodes: ISankeyNode[] = [
        { label: 'Started Flow', id: 'initial' },
        { label: 'Started Flow', id: 'entered' },
        { label: 'Retained', id: 'retained', pct: retainedPct },
      ];

      // const totalEntered = this.selectedSessions.length
      const links: ISankeyLink[] = [
        { source: 'initial', target: 'entered', value: totalSessions },
        { source: 'entered', target: 'retained', value: totalSaved },
        { source: 'entered', target: 'canceled', value: totalCanceled },
      ];

      if (pauseOutcomes.length) {
        const totalPauses = pauseOutcomes.reduce((sum, d) => sum + d.count, 0);
        nodes.push({ label: 'Paused Account', id: 'paused' });
        links.push({
          source: 'retained',
          target: 'paused',
          value: totalPauses,
        });
        pauseOutcomes.forEach((d) => {
          const duration = d.pauseDuration as number;
          const noun = duration === 1 ? 'month' : 'months';
          nodes.push({
            label: `${duration} ${noun}`,
            id: `pause-${duration}`,
          });
          links.push({
            source: 'paused',
            target: `pause-${duration}`,
            value: d.count,
          });
        });
      }

      if (discountOutcomes.length) {
        const totalDiscounts = discountOutcomes.reduce((sum, d) => sum + d.count, 0);
        nodes.push({ label: 'Accepted Discount', id: 'discount' });
        links.push({
          source: 'retained',
          target: 'discount',
          value: totalDiscounts,
        });
        discountOutcomes.forEach((d) => {
          const couponId = d.couponId as string;
          const label = this.couponIdToString(d.couponId);
          nodes.push({
            label,
            id: `discount-${couponId}`,
          });
          links.push({
            source: 'discount',
            target: `discount-${couponId}`,
            value: d.count,
          });
        });
      }

      if (contactOutcome) {
        nodes.push({ label: 'Contacted Support', id: 'contact' });
        links.push({
          source: 'retained',
          target: 'contact',
          value: contactOutcome.count,
        });
      }

      if (extendedTrialOutcome) {
        nodes.push({ label: 'Extended Trial', id: 'extended-trial' });
        links.push({
          source: 'retained',
          target: 'extended-trial',
          value: extendedTrialOutcome.count,
        });
      }

      if (changedPlanOutcome) {
        nodes.push({ label: 'Changed Plan', id: 'changed-plan' });
        links.push({
          source: 'retained',
          target: 'changed-plan',
          value: changedPlanOutcome.count,
        });
      }

      if (redirectedOutcome) {
        nodes.push({ label: 'Redirected', id: 'redirected' });
        links.push({
          source: 'retained',
          target: 'redirected',
          value: redirectedOutcome.count,
        });
      }

      if (abandonedOutcome) {
        nodes.push({ label: 'Abandoned', id: 'abandoned' });
        links.push({
          source: 'retained',
          target: 'abandoned',
          value: abandonedOutcome.count,
        });
      }

      nodes.push({ label: 'Cancelled', id: 'canceled', pct: canceledPct });

      return {
        nodes,
        links,
      };
    },
    flowTree() {
      if (this.loading || !this.sessionOutcomes.length) {
        return null;
      }
      const canceledOutcome = this.sessionOutcomes.find((d) => d.canceled);
      const abandonedOutcome = this.sessionOutcomes.find((d) => d.aborted);
      const pauseOutcomes = this.sessionOutcomes.filter((d) => d.pauseDuration).sort((a, b) => descending(a.pauseDuration, b.pauseDuration));
      const discountOutcomes = this.sessionOutcomes.filter((d) => d.couponId).sort((a, b) => descending(a.count, b.count));
      const contactOutcome = this.sessionOutcomes.find((d) => d.offerType === OFFER_TYPE.CONTACT);

      const totalSessions = this.sessionOutcomes.reduce((sum, d) => sum + d.count, 0);
      const totalCanceled = canceledOutcome?.count || 0;
      const totalSaved = totalSessions - totalCanceled;

      const nodes: ITreeNode[] = [
        { label: 'Started Cancel Flow', id: 'initial', fill: '#D8DFF6', stroke: '#3C5CCF' },
        { label: 'Cancelled', id: 'cancelled', parentId: 'initial', fill: '#FBC1B5', stroke: '#FF715B', count: totalCanceled },
        { label: `Didn't Cancel`, id: 'retained', parentId: 'initial', fill: '#A6D6BE', stroke: '#1E694A' },
      ];

      if (abandonedOutcome) {
        nodes.push({ label: 'Abandoned', id: 'abadoned', parentId: 'retained', fill: '#BAC5EE', stroke: '#D8DFF6', count: abandonedOutcome.count });
      }

      if (contactOutcome) {
        nodes.push({ label: 'Support', id: 'contact', parentId: 'retained', fill: '#BAC5EE', stroke: '#D8DFF6', count: contactOutcome.count });
      }

      if (pauseOutcomes.length) {
        nodes.push({ label: 'Paused Account', id: 'paused', parentId: 'retained', fill: '#F8E2A9', stroke: '#F7B200' });
        const pauseNodes = pauseOutcomes.map((d) => {
          const duration = d.pauseDuration as number;
          const noun = duration === 1 ? 'Month' : 'Months';
          return {
            label: `${duration} ${noun} Pause`,
            id: `pause-${duration}`,
            parentId: 'paused',
            count: d.count,
            fill: '#F8E2A9',
            stroke: '#F7B200',
          };
        });
        nodes.push(...pauseNodes);
      }

      if (discountOutcomes.length) {
        nodes.push({ label: 'Discounts Accepted', id: 'discounted', parentId: 'retained', fill: '#E0BFE7', stroke: '#BB6BD9' });
        const couponNodes = discountOutcomes.map((d) => {
          const couponId = d.couponId as string;
          const label = this.couponIdToString(couponId);
          return {
            id: `discount-${couponId}`,
            label,
            parentId: 'discounted',
            count: d.count,
            fill: '#E0BFE7',
            stroke: '#BB6BD9',
          };
        });
        nodes.push(...couponNodes);
      }
      return nodes;
    },
  },
  methods: {
    getSegmentName(segmentId: string): ISegment | undefined {
      if (!segmentId) {
        return undefined;
      }
      const segment = this.$store.state.segments[segmentId];
      if (segment) {
        return segment.name;
      }
      return undefined;
    },
    exportSessionOutcomes() {
      this.$store.dispatch(EXPORT_DATA, {
        data: this.sessionOutcomes,
        name: 'session-outcomes',
        csv: true,
        fields: ['count', 'trial', 'canceled', 'aborted', 'offerType', 'pauseDuration', 'couponId'],
      });
    },
    exportChart() {
      if (this.chartInstance) {
        this.chartInstance.exportToPng();
      }
    },
    handleDateChange(value: [Date, Date]): void {
      this.startDate = value[0];
      this.endDate = value[1];
      this.fetchOutcomes();
      this.fetchReactivationStats();
    },
    onFilterUpdate(newFilter: Record<string, string[]>) {
      this.filter = newFilter;
      this.fetchOutcomes();
      this.fetchReactivationStats();
    },
    async fetchOutcomes() {
      this.loading = true;
      const mappedFilter = Object.keys(this.filter).reduce(
        (acc, k) => {
          if (this.filter[k].length === 1) {
            acc[k] = this.filter[k][0];
          }
          return acc;
        },
        {} as Record<string, any>
      );
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      const outcomes = await this.$store.dispatch(RETURN_SESSION_OUTCOMES, { filter: mappedFilter });
      this.sessionOutcomes = outcomes;
      this.filteredOutcomes = [...outcomes];
      this.loading = false;
    },
    async fetchReactivationStats() {
      const mappedFilter = Object.keys(this.filter).reduce(
        (acc, k) => {
          if (this.filter[k].length === 1) {
            acc[k] = this.filter[k][0];
          }
          return acc;
        },
        {} as Record<string, any>
      );
      try {
        this.localReactivationStatsLoadState = LOAD_STATE.LOADING;
        const stats = await StatsService.reactivationStats({ filter: mappedFilter, breakdown: [SESSION.SAVE_TYPE] });
        if (stats) {
          this.localReactivationStats = stats;
        }
        this.localReactivationStatsLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.localReactivationStatsLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
  },
});
